import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Badge, Empty, Field, Modal, Skeleton, useToast } from '../components/ui';
import {
  ApiError,
  MIN_ACTIVITY_CHARS,
  api,
  type DayDetail,
  type Segment,
  type StatusResponse,
} from '../lib/api';
import { clockTime, hm, longDate, prettyDate, stamp } from '../lib/format';
import { useAuth } from '../lib/auth';

export function TodayPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [manualOpen, setManualOpen] = useState(false);
  const [editing, setEditing] = useState<Segment | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['status'],
    queryFn: () => api.get<StatusResponse>('/timesheet/status'),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['status'] });
    void qc.invalidateQueries({ queryKey: ['report'] });
    void qc.invalidateQueries({ queryKey: ['day'] });
  };

  const tz = data?.timezone ?? me?.timezone;

  const workedToday = (data?.day.segments ?? [])
    .filter((s) => s.kind === 'WORK' && s.endedAt)
    .reduce(
      (acc, s) => acc + (new Date(s.endedAt!).getTime() - new Date(s.startedAt).getTime()) / 60000,
      0,
    );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Olá, {me?.name.split(' ')[0]}</h1>
          <p style={{ textTransform: 'capitalize' }}>
            {data ? longDate(data.today) : <span className="sr-only">carregando</span>}
          </p>
        </div>
        <button className="btn btn--primary" disabled={!data} onClick={() => setManualOpen(true)}>
          Lançar horário
        </button>
      </div>

      <div className="grid grid--2">
        <section className="card">
          <div className="card__head">
            <h2>Registros de hoje</h2>
            {workedToday > 0 ? <Badge tone="ok">{hm(workedToday)}</Badge> : null}
          </div>
          <div className="card__body--tight">
            {isLoading ? (
              <div style={{ padding: 20, display: 'grid', gap: 10 }}>
                <Skeleton height={44} />
                <Skeleton height={44} />
              </div>
            ) : data && data.day.segments.length > 0 ? (
              <ul className="timeline">
                {data.day.segments.map((s) => (
                  <SegmentRow key={s.id} segment={s} tz={tz} onEdit={() => setEditing(s)} />
                ))}
              </ul>
            ) : (
              <Empty
                title="Nenhum registro hoje"
                hint="Use “Lançar horário” para informar a entrada e a saída."
              />
            )}
          </div>
        </section>

        <ActivityCard
          workDate={data?.today ?? ''}
          initial={data?.day.note?.content ?? ''}
          hasSegments={(data?.day.segments.length ?? 0) > 0}
          disabled={!data}
        />
      </div>

      {manualOpen && data ? (
        <SegmentModal
          workDate={data.today}
          editableFrom={data.editableFrom}
          onClose={() => setManualOpen(false)}
          onSaved={() => {
            setManualOpen(false);
            invalidate();
          }}
        />
      ) : null}

      {editing ? (
        <SegmentModal
          segment={editing}
          workDate={data!.today}
          editableFrom={data!.editableFrom}
          tz={tz}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      ) : null}
    </>
  );
}

// ------------------------------------------------------------------ linha

function SegmentRow({
  segment,
  tz,
  onEdit,
}: {
  segment: Segment;
  tz?: string;
  onEdit: () => void;
}) {
  const minutes = segment.endedAt
    ? Math.round(
        (new Date(segment.endedAt).getTime() - new Date(segment.startedAt).getTime()) / 60000,
      )
    : null;

  return (
    <li className="timeline__item">
      <span className="timeline__rail">
        <span
          className={`timeline__bullet${segment.kind === 'BREAK' ? ' timeline__bullet--break' : ''}`}
        />
      </span>

      <div>
        <div className="timeline__time tnum">
          {clockTime(segment.startedAt, tz)} → {segment.endedAt ? clockTime(segment.endedAt, tz) : '—'}
        </div>
        <div className="timeline__meta">
          {segment.kind === 'BREAK' ? 'Pausa' : 'Trabalho'}
          {segment.version > 1 ? ` · editado ${segment.version - 1}×` : ''}
          {segment.description ? ` · ${segment.description}` : ''}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="timeline__dur tnum">{minutes === null ? '—' : hm(minutes)}</span>
        <button className="btn btn--ghost btn--sm" onClick={onEdit} title="Editar registro">
          Editar
        </button>
      </div>
    </li>
  );
}

// ------------------------------------------------------------------ atividade

function ActivityCard({
  workDate,
  initial,
  hasSegments,
  disabled,
}: {
  workDate: string;
  initial: string;
  hasSegments: boolean;
  disabled: boolean;
}) {
  const [value, setValue] = useState(initial);
  const [saved, setSaved] = useState<'idle' | 'saving' | 'done'>('idle');
  const toast = useToast();
  const qc = useQueryClient();
  const timer = useRef<number>();
  const lastSaved = useRef(initial);

  useEffect(() => {
    setValue(initial);
    lastSaved.current = initial;
  }, [initial, workDate]);

  const save = useMutation({
    mutationFn: (content: string) => api.put('/timesheet/notes', { workDate, content }),
    onMutate: () => setSaved('saving'),
    onSuccess: (_d, content) => {
      lastSaved.current = content;
      setSaved('done');
      void qc.invalidateQueries({ queryKey: ['status'] });
      setTimeout(() => setSaved('idle'), 2500);
    },
    onError: (e: ApiError) => {
      setSaved('idle');
      toast(e.message, 'error');
    },
  });

  // Com horas lançadas, a atividade não pode ficar vazia (o servidor também recusa).
  const tooShort = value.trim().length < MIN_ACTIVITY_CHARS;
  const blocked = hasSegments && tooShort;

  // Autosave com debounce: o estagiário não precisa lembrar de clicar em salvar.
  useEffect(() => {
    if (disabled || blocked || value === lastSaved.current) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => save.mutate(value), 1200);
    return () => window.clearTimeout(timer.current);
  }, [value, disabled, blocked]);

  return (
    <section className="card">
      <div className="card__head">
        <h2>Atividade do dia</h2>
        <span style={{ fontSize: '0.8rem', color: 'var(--faint)' }} aria-live="polite">
          {saved === 'saving'
            ? 'Salvando…'
            : saved === 'done'
              ? 'Salvo'
              : blocked && value !== lastSaved.current
                ? 'Não salvo'
                : 'Salva automaticamente'}
        </span>
      </div>
      <div className="card__body">
        <Field
          label="O que você fez hoje"
          hint={`Obrigatório para lançar horários (mínimo de ${MIN_ACTIVITY_CHARS} caracteres). Fica visível para o administrador no relatório.`}
          error={
            blocked
              ? `Descreva a atividade com pelo menos ${MIN_ACTIVITY_CHARS} caracteres: o dia já tem horários lançados.`
              : undefined
          }
        >
          <textarea
            value={value}
            disabled={disabled}
            maxLength={5000}
            placeholder="Ex.: ajustes no módulo de cadastro, revisão do PR #214, reunião de alinhamento."
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => {
              if (!blocked && value !== lastSaved.current) save.mutate(value);
            }}
          />
        </Field>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ modal

function SegmentModal({
  segment,
  workDate,
  editableFrom,
  tz,
  onClose,
  onSaved,
}: {
  segment?: Segment;
  workDate: string;
  editableFrom: string;
  tz?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const editMode = Boolean(segment);

  const segmentDate = segment
    ? new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date(segment.startedAt))
    : workDate;
  const [date, setDate] = useState(segmentDate);
  const [start, setStart] = useState(segment ? clockTime(segment.startedAt, tz) : '09:00');
  const [end, setEnd] = useState(
    segment?.endedAt ? clockTime(segment.endedAt, tz) : '18:00',
  );
  const [description, setDescription] = useState(segment?.description ?? '');
  const [reason, setReason] = useState('');
  const [activity, setActivity] = useState('');
  const activityTouched = useRef(false);

  // Atividade do dia escolhido: pré-preenche se já existir.
  const day = useQuery({
    queryKey: ['day', date],
    queryFn: () => api.get<DayDetail>(`/timesheet/days/${date}`),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(date),
  });
  const existingActivity = day.data?.note?.content ?? '';
  useEffect(() => {
    if (!activityTouched.current) setActivity(existingActivity);
  }, [existingActivity, date]);

  const activityOk = activity.trim().length >= MIN_ACTIVITY_CHARS;

  const crossesMidnight = end <= start;
  // O servidor também recusa; aqui é só para o estagiário saber antes de preencher tudo.
  const locked = date < editableFrom || (segment ? segmentDate < editableFrom : false);

  const mutation = useMutation({
    mutationFn: () =>
      editMode
        ? api.patch(`/timesheet/segments/${segment!.id}`, {
            workDate: date,
            start,
            end,
            description: description || null,
            activity: activity.trim(),
            reason,
          })
        : api.post('/timesheet/segments', {
            workDate: date,
            start,
            end,
            description: description || null,
            activity: activity.trim(),
          }),
    onSuccess: () => {
      toast(editMode ? 'Registro atualizado.' : 'Registro lançado.', 'success');
      onSaved();
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/timesheet/segments/${segment!.id}`, { reason }),
    onSuccess: () => {
      toast('Registro excluído. A alteração ficou na trilha de auditoria.', 'success');
      onSaved();
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });

  // Lançar é o fluxo normal; o motivo só é exigido ao alterar o que já foi lançado.
  const valid =
    !locked && Boolean(start && end) && activityOk && (!editMode || reason.trim().length >= 3);
  // Excluir não depende da atividade.
  const canDelete = !locked && reason.trim().length >= 3;

  return (
    <Modal
      title={editMode ? 'Editar registro' : 'Lançar horário'}
      onClose={onClose}
      footer={
        <>
          {editMode ? (
            <button
              className="btn btn--ghost"
              style={{ marginRight: 'auto', color: 'var(--danger)' }}
              disabled={!canDelete || remove.isPending}
              onClick={() => remove.mutate()}
            >
              Excluir
            </button>
          ) : null}
          <button className="btn btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            disabled={!valid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <Field
        label="Data"
        hint={`Datas antes de ${prettyDate(editableFrom)} estão travadas para lançamento.`}
        error={locked ? 'Esta data está travada. Peça ajuste ao administrador.' : undefined}
      >
        <input
          type="date"
          value={date}
          min={editableFrom}
          onChange={(e) => setDate(e.target.value)}
        />
      </Field>

      <div className="form-row">
        {/* input nativo: aceita digitação, seta do teclado e o picker do sistema */}
        <Field label="Entrada">
          <input type="time" step={60} value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <Field
          label="Saída"
          hint={crossesMidnight ? 'Vira o dia: a saída será contada na madrugada seguinte.' : undefined}
        >
          <input type="time" step={60} value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
      </div>

      <Field
        label="Atividade do dia"
        hint={
          existingActivity
            ? 'Já preenchida para este dia. Ajuste se quiser; vale para o dia todo.'
            : `Obrigatória. O que você fez neste dia (mínimo de ${MIN_ACTIVITY_CHARS} caracteres).`
        }
        error={
          activity.length > 0 && !activityOk
            ? `Descreva com pelo menos ${MIN_ACTIVITY_CHARS} caracteres.`
            : undefined
        }
      >
        <textarea
          maxLength={5000}
          rows={3}
          value={activity}
          disabled={day.isLoading}
          placeholder="Ex.: ajustes no módulo de cadastro, revisão do PR #214, reunião de alinhamento."
          onChange={(e) => {
            activityTouched.current = true;
            setActivity(e.target.value);
          }}
        />
      </Field>

      <Field label="Descrição do período (opcional)">
        <input
          type="text"
          maxLength={2000}
          value={description}
          placeholder="Ex.: período da manhã"
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      {editMode ? (
        <Field
          label="Motivo"
          hint="Obrigatório. Fica gravado na trilha de auditoria junto com a data e hora do lançamento."
          error={reason.length > 0 && reason.trim().length < 3 ? 'Descreva com pelo menos 3 caracteres.' : undefined}
        >
          <input
            type="text"
            maxLength={500}
            value={reason}
            placeholder="Ex.: saída digitada errada"
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      ) : (
        <p style={{ fontSize: '0.78rem', color: 'var(--faint)' }}>
          Para registrar uma pausa, lance dois períodos: antes e depois dela.
        </p>
      )}

      {segment ? (
        <p style={{ fontSize: '0.78rem', color: 'var(--faint)' }}>
          Versão {segment.version} · criado em {stamp(segment.createdAt, tz)}
          {segment.version > 1 ? ` · última alteração em ${stamp(segment.updatedAt, tz)}` : ''}
        </p>
      ) : null}
    </Modal>
  );
}
